import {
  ArrowUpOnSquareIcon,
  Cog6ToothIcon,
  CubeIcon,
  DocumentIcon,
  HomeModernIcon,
  Squares2X2Icon,
  SwatchIcon,
  TruckIcon,
  UsersIcon,
} from "@heroicons/react/24/outline";
import React from "react";
import { useTranslation } from "react-i18next";
import { Link, useLocation } from "react-router-dom";
import { AuthService } from "../../Modules/Auth/services/AuthService";

interface SidebarItemProps {
  to: string;
  label: string;
  icon: string;
  active?: boolean;
}

const SidebarItem: React.FC<SidebarItemProps> = ({
  to,
  label,
  icon,
  active,
}) => {
  const baseColor = active
    ? "bg-blue-700 text-white"
    : "bg-blue-400 text-white hover:text-blue-800 hover:bg-yellow-500 text-white";
  const iconMap: Record<string, JSX.Element> = {
    grid: <Squares2X2Icon className="h-4 w-4" />,
    company: <SwatchIcon className="h-4 w-4" />,
    vehicles: <CubeIcon className="h-4 w-4" />,
    drivers: <TruckIcon className="h-4 w-4" />,
    users: <UsersIcon className="h-4 w-4" />,
    activity: <HomeModernIcon className="h-4 w-4" />,
    settings: <Cog6ToothIcon className="h-5 w-5" />,
    document: <DocumentIcon className="h-5 w-5" />,
    logout: <ArrowUpOnSquareIcon className="h-5 w-5 rotate-90" />,
  };

  return (
    <li
      className={`flex py-3 w-full justify-between cursor-pointer items-center px-3 ${baseColor}`}
    >
      <Link
        to={to}
        className={`flex items-center focus:outline-none`}
      >
        {iconMap[icon]}
        <span className="ml-2">{label}</span>
      </Link>
    </li>
  );
};

export const SidebarMenu: React.FC = () => {
  const logout = () => AuthService.logout();
  const location = useLocation();
  const isActive = (path: string) => location.pathname.startsWith(path);
  const { t } = useTranslation();
  let allowedMain = AuthService.isManagementAllowed();
  return (
    <div className="">
      <ul className="my-3">
        {allowedMain && (
          <SidebarItem
            to="/services/accounts/home"
            label={t('menu_dashboard')}
            icon="grid"
            active={isActive("/services/accounts/home")}
          />
        )}
        {allowedMain && (
          <SidebarItem
            to="/services/accounts/users"
            label={t('menu_users')}
            icon="users"
            active={isActive("/services/accounts/users")}
          />
        )}
        <SidebarItem
          to="/services/accounts/companies"
          label={t('menu_companies')}
          icon="company"
          active={isActive("/services/accounts/companies")}
        />
        <SidebarItem
          to="/services/vehicles/home"
          label={t('menu_trucks')}
          icon="vehicles"
          active={isActive("/services/vehicles/home")}
        />
        <SidebarItem
          to="/services/vehicles/drivers"
          label={t('menu_drivers')}
          icon="drivers"
          active={isActive("/services/vehicles/drivers")}
        />
        <SidebarItem
          to="/services/accounts/approvals"
          label={t('menu_approvals')}
          icon="company"
          active={isActive("/services/accounts/approvals")}
        />
        {allowedMain && (
          <SidebarItem
            to="/services/settings/home"
            label={t('menu_activity')}
            icon="activity"
            active={isActive("/services/settings/home")}
          />
        )}
        <SidebarItem
          to="/services/settings/account"
          label={t('menu_settings')}
          icon="settings"
          active={isActive("/services/settings/account")}
        />
        <div className="" onClick={() => logout()}>
          <SidebarItem to="/auth/login" label={t('menu_logout')} icon="logout" />
        </div>
      </ul>
      <div className="flex justify-center mt-48 mb-4 w-full">
        <div className="relative" />
      </div>
    </div>
  );
};
